import { useEffect, useState } from 'react';
import { reservasApi } from '../lib/api';

interface MesaInfo { id: number; numMesa: number; capacidad: number; ocupada: boolean; }
interface SalaInfo { sala: string; mesas: MesaInfo[]; }
interface MesoneroInfo { id: number; codVendedor: number; nombre: string; }

export function CheckinPanel({
  reservaId, sucursalId, numPersonas, onDone, onCancel,
}: {
  reservaId: number;
  sucursalId?: number;
  numPersonas: number;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [salas, setSalas] = useState<SalaInfo[]>([]);
  const [mesoneros, setMesoneros] = useState<MesoneroInfo[]>([]);
  const [salaSel, setSalaSel] = useState<string>('');
  const [mesaSel, setMesaSel] = useState<MesaInfo | null>(null);
  const [mesoneroSel, setMesoneroSel] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sucursalId) { setError('La reserva no tiene sucursal asignada'); setLoading(false); return; }
    Promise.all([
      reservasApi.sucursalMesas(sucursalId),
      reservasApi.sucursalMesoneros(sucursalId),
    ])
      .then(([mesasData, mesonerosData]) => {
        setSalas(mesasData);
        if (mesasData.length > 0) setSalaSel(mesasData[0].sala);
        setMesoneros(mesonerosData);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [sucursalId]);

  const mesasDeSala = salas.find((s) => s.sala === salaSel)?.mesas ?? [];

  async function confirmar() {
    if (!mesaSel) return;
    setSaving(true);
    setError(null);
    try {
      await reservasApi.checkin(reservaId, mesaSel.id, mesoneroSel ? Number(mesoneroSel) : undefined);
      onDone();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border border-brand-300 bg-brand-50/40 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-800">Asignar mesa · {numPersonas} personas</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700 text-sm">✕</button>
      </div>

      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{error}</div>}

      {loading ? (
        <div className="text-sm text-slate-500 py-4 text-center">Cargando mesas…</div>
      ) : (
        <>
          {/* Selector de sala */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Sala</label>
            <select
              value={salaSel}
              onChange={(e) => { setSalaSel(e.target.value); setMesaSel(null); }}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              {salas.map((s) => (
                <option key={s.sala} value={s.sala}>{s.sala} ({s.mesas.length} mesas)</option>
              ))}
            </select>
          </div>

          {/* Grilla de mesas */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Mesa</label>
            <div className="grid grid-cols-5 sm:grid-cols-8 gap-1.5 max-h-48 overflow-y-auto p-1">
              {mesasDeSala.map((m) => {
                const sel = mesaSel?.id === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setMesaSel(m)}
                    title={m.ocupada ? 'Ocupada ahora' : `Capacidad: ${m.capacidad || '—'}`}
                    className={`aspect-square rounded-md border text-sm font-medium flex flex-col items-center justify-center transition-colors
                      ${sel ? 'border-brand-600 bg-brand-600 text-white ring-2 ring-brand-300'
                        : m.ocupada ? 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100'
                        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
                  >
                    <span>{m.numMesa}</span>
                    {m.ocupada && <span className="text-[9px] leading-none">ocup.</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {mesaSel?.ocupada && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              ⚠ La mesa {mesaSel.numMesa} ya tiene un cliente sentado. Podés asignarla igual si lo confirmás.
            </div>
          )}

          {/* Selector de mesonero (opcional) */}
          <div>
            <label className="block text-xs text-slate-500 mb-1">Mesonero (opcional)</label>
            <select
              value={mesoneroSel}
              onChange={(e) => setMesoneroSel(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">— Sin asignar —</option>
              {mesoneros.map((m) => (
                <option key={m.id} value={m.id}>{m.nombre}</option>
              ))}
            </select>
            {mesoneros.length === 0 && (
              <p className="text-xs text-slate-400 mt-1">No hay mesoneros asignados a esta sucursal.</p>
            )}
          </div>

          {/* Acciones */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={confirmar}
              disabled={!mesaSel || saving}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white text-sm rounded-md hover:bg-emerald-700 disabled:opacity-40 font-medium"
            >
              {saving ? 'Asignando…' : mesaSel ? `Confirmar llegada · Mesa ${mesaSel.numMesa}` : 'Elegí una mesa'}
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="px-4 py-2 border border-slate-300 text-slate-700 text-sm rounded-md hover:bg-slate-100"
            >
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
